import {
    Quote as QuoteIcon,
    RefreshCw,
    Loader2,
    Languages,
    Plus,
    X,
    Send,
} from 'lucide-react'
import React, { useEffect, useState, useCallback } from 'react'
import { Typogaphy, Button } from 'vnetwork-ui'
import { useTranslation } from 'react-i18next'
import { getRandomQuote, createQuote } from '@/modules/Dashboard/services'
import { RandomQuoteResponse } from '@/modules/Dashboard/types'

interface QuoteData {
    quote: string
    author: string
    translatedQuote?: string
    source?: 'user' | 'api'
    contributedBy?: string
}

// Fallback quotes (used when both APIs fail)
const FALLBACK_QUOTES: QuoteData[] = [
    {
        quote: 'Alone we can do so little; together we can do so much.',
        author: 'Helen Keller',
    },
    {
        quote: 'Coming together is a beginning, staying together is progress, and working together is success.',
        author: 'Henry Ford',
    },
    {
        quote: 'Talent wins games, but teamwork and intelligence win championships.',
        author: 'Michael Jordan',
    },
    {
        quote: "Great things in business are never done by one person; they're done by a team of people.",
        author: 'Steve Jobs',
    },
    {
        quote: 'The only way to do great work is to love what you do.',
        author: 'Steve Jobs',
    },
    {
        quote: 'Success is not final, failure is not fatal: it is the courage to continue that counts.',
        author: 'Winston Churchill',
    },
    {
        quote: 'Có công mài sắt, có ngày nên kim.',
        author: 'Tục ngữ Việt Nam',
    },
    {
        quote: 'Một cây làm chẳng nên non, ba cây chụm lại nên hòn núi cao.',
        author: 'Tục ngữ Việt Nam',
    },
    {
        quote: 'Thất bại là mẹ thành công.',
        author: 'Tục ngữ Việt Nam',
    },
    {
        quote: 'The best way to predict the future is to create it.',
        author: 'Peter Drucker',
    },
]

// Get random fallback quote
const getRandomFallbackQuote = (): QuoteData => {
    const index = Math.floor(Math.random() * FALLBACK_QUOTES.length)
    return FALLBACK_QUOTES[index]
}

// Cache key for today's quote
const QUOTE_CACHE_PREFIX = 'quote_of_day_'
const getTodayKey = () => {
    const today = new Date()
    return `${QUOTE_CACHE_PREFIX}${today.getFullYear()}_${today.getMonth()}_${today.getDate()}`
}

// Clear old quote cache keys (keep only today's)
const clearOldQuoteCache = () => {
    const todayKey = getTodayKey()
    const keysToRemove: string[] = []

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(QUOTE_CACHE_PREFIX) && key !== todayKey) {
            keysToRemove.push(key)
        }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key))
}

// History key for shown quotes (to avoid repeats)
const getHistoryKey = () => 'quote_history'

// Get shown quotes from history
const getShownQuotes = (): string[] => {
    try {
        const history = localStorage.getItem(getHistoryKey())
        return history ? JSON.parse(history) : []
    } catch {
        return []
    }
}

// Add quote to history (store quote content hash to identify)
const addToHistory = (quoteContent: string) => {
    const history = getShownQuotes()
    const hash = quoteContent.substring(0, 50) // Use first 50 chars as identifier
    if (!history.includes(hash)) {
        history.push(hash)
        // Keep only last 100 quotes in history
        if (history.length > 100) {
            history.shift()
        }
        localStorage.setItem(getHistoryKey(), JSON.stringify(history))
    }
}

// Check if quote was already shown
const isQuoteShown = (quoteContent: string): boolean => {
    const history = getShownQuotes()
    const hash = quoteContent.substring(0, 50)
    return history.includes(hash)
}

// Fetch quote from external API (type.fit - static quotes)
const fetchExternalQuote = async (): Promise<QuoteData | null> => {
    try {
        const response = await fetch('https://type.fit/api/quotes')
        if (response.ok) {
            const data = await response.json()
            if (data && data.length > 0) {
                const randomIndex = Math.floor(Math.random() * data.length)
                const quote = data[randomIndex]
                return {
                    quote: quote.text,
                    author:
                        quote.author?.replace(', type.fit', '') || 'Unknown',
                    source: 'api',
                }
            }
        }
    } catch {
        // API failed
    }

    return null
}

const QuoteOfTheDay: React.FC = () => {
    const { t } = useTranslation('dashboard')
    const [quote, setQuote] = useState<QuoteData | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isTranslating, setIsTranslating] = useState(false)
    const [showTranslation, setShowTranslation] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Contribute modal state
    const [showContributeModal, setShowContributeModal] = useState(false)
    const [newQuote, setNewQuote] = useState('')
    const [newAuthor, setNewAuthor] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitSuccess, setSubmitSuccess] = useState(false)

    // Track last source for alternating
    const [lastSource, setLastSource] = useState<
        'internal' | 'external' | null
    >(null)

    // Helper to fetch a single quote attempt
    const fetchSingleQuote = async (
        preferInternal: boolean
    ): Promise<{
        quote: QuoteData | null
        source: 'internal' | 'external'
    }> => {
        if (preferInternal) {
            // Try internal/backend first
            try {
                const response: RandomQuoteResponse = await getRandomQuote()
                return {
                    quote: {
                        quote: response.quote.content,
                        author: response.quote.author,
                        source: response.source,
                        contributedBy:
                            response.quote.created_by_name || undefined,
                    },
                    source: 'internal',
                }
            } catch {
                // Backend failed, try external
                const externalQuote = await fetchExternalQuote()
                return { quote: externalQuote, source: 'external' }
            }
        } else {
            // Try external first
            const externalQuote = await fetchExternalQuote()
            if (externalQuote) {
                return { quote: externalQuote, source: 'external' }
            }
            // External failed, try backend
            try {
                const response: RandomQuoteResponse = await getRandomQuote()
                return {
                    quote: {
                        quote: response.quote.content,
                        author: response.quote.author,
                        source: response.source,
                        contributedBy:
                            response.quote.created_by_name || undefined,
                    },
                    source: 'internal',
                }
            } catch {
                return { quote: null, source: 'external' }
            }
        }
    }

    // Fetch quote - tries backend first (when available), then external API, then fallback
    const fetchQuote = useCallback(
        async (forceNew = false) => {
            // Clear old cache keys on new day
            clearOldQuoteCache()

            // Check cache first (unless forcing new)
            if (!forceNew) {
                const cached = localStorage.getItem(getTodayKey())
                if (cached) {
                    try {
                        const cachedQuote = JSON.parse(cached) as QuoteData
                        setQuote(cachedQuote)
                        return
                    } catch {
                        // Invalid cache, fetch new
                    }
                }
            }

            setIsLoading(true)
            setError(null)

            // Determine source: random 1-10, even=internal, odd=external
            // But if last was internal, force external (alternate)
            const randomNum = Math.floor(Math.random() * 10) + 1 // 1-10
            const isEven = randomNum % 2 === 0
            let useInternal = isEven

            // If last call was internal, force external this time
            if (lastSource === 'internal') {
                useInternal = false
            }

            // Try up to 3 times to get a non-repeated quote
            const maxRetries = 3
            let resultQuote: QuoteData | null = null
            let usedSource: 'internal' | 'external' = 'external'

            for (let attempt = 0; attempt < maxRetries; attempt++) {
                const result = await fetchSingleQuote(useInternal)
                resultQuote = result.quote
                usedSource = result.source

                // If no quote or quote wasn't shown before, use it
                if (!resultQuote || !isQuoteShown(resultQuote.quote)) {
                    break
                }

                // Quote was shown before, alternate source for next attempt
                useInternal = !useInternal
            }

            // Set result or fallback
            if (resultQuote) {
                // Add to history so we don't show it again soon
                addToHistory(resultQuote.quote)
                setQuote(resultQuote)
                setLastSource(usedSource)
                // Always save to cache (so F5 shows the latest quote)
                localStorage.setItem(getTodayKey(), JSON.stringify(resultQuote))
            } else {
                // All sources failed, use local fallback (also check history)
                let fallback = getRandomFallbackQuote()
                for (let i = 0; i < FALLBACK_QUOTES.length; i++) {
                    if (!isQuoteShown(fallback.quote)) break
                    fallback = getRandomFallbackQuote()
                }
                addToHistory(fallback.quote)
                setQuote(fallback)
                localStorage.setItem(getTodayKey(), JSON.stringify(fallback))
            }
            setIsLoading(false)
        },
        [lastSource]
    )

    // Fetch on mount
    useEffect(() => {
        fetchQuote()
    }, [fetchQuote])

    const handleRefresh = () => {
        setShowTranslation(false)
        fetchQuote(true)
    }

    // Translate quote to Vietnamese
    const translateQuote = useCallback(async () => {
        if (!quote || quote.translatedQuote) {
            setShowTranslation(!showTranslation)
            return
        }

        setIsTranslating(true)

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(quote.quote)}&langpair=en|vi`
            )

            if (!response.ok) throw new Error('Translation failed')

            const data = await response.json()

            if (
                data.responseStatus === 200 &&
                data.responseData?.translatedText
            ) {
                setQuote((prev) =>
                    prev
                        ? {
                              ...prev,
                              translatedQuote: data.responseData.translatedText,
                          }
                        : null
                )
                setShowTranslation(true)
            }
        } catch (err) {
            console.error('Translation error:', err)
        } finally {
            setIsTranslating(false)
        }
    }, [quote, showTranslation])

    // Submit new quote
    const handleSubmitQuote = async () => {
        if (!newQuote.trim()) return

        setIsSubmitting(true)
        try {
            await createQuote({
                content: newQuote.trim(),
                author: newAuthor.trim() || 'Unknown',
            })
            setSubmitSuccess(true)
            setNewQuote('')
            setNewAuthor('')
            setTimeout(() => {
                setShowContributeModal(false)
                setSubmitSuccess(false)
            }, 2000)
        } catch (err) {
            console.error('Failed to submit quote:', err)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                        <QuoteIcon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <Typogaphy className="font-semibold text-gray-800">
                            {t('quote.title')}
                        </Typogaphy>
                        <Typogaphy className="text-[10px] text-gray-400">
                            {new Date().toLocaleDateString('vi-VN', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                            })}
                        </Typogaphy>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {/* Contribute button */}
                    <button
                        onClick={() => setShowContributeModal(true)}
                        className="p-2 rounded-full hover:bg-amber-50 transition-colors group"
                        title={t('quote.contribute')}
                        data-tour="quote-contribute-button"
                    >
                        <Plus className="w-4 h-4 text-amber-600" />
                    </button>

                    {/* Translate button */}
                    <button
                        onClick={translateQuote}
                        disabled={isTranslating || !quote}
                        className={`p-2 rounded-full hover:bg-amber-50 transition-colors group disabled:opacity-50 ${
                            showTranslation ? 'bg-amber-100' : ''
                        }`}
                        title={
                            showTranslation
                                ? t('quote.show-original', 'Xem bản gốc')
                                : t('quote.translate', 'Dịch sang tiếng Việt')
                        }
                    >
                        {isTranslating ? (
                            <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
                        ) : (
                            <Languages
                                className={`w-4 h-4 ${showTranslation ? 'text-amber-700' : 'text-amber-600'}`}
                            />
                        )}
                    </button>

                    {/* Refresh button */}
                    <button
                        onClick={handleRefresh}
                        disabled={isLoading}
                        className="p-2 rounded-full hover:bg-amber-50 transition-colors group disabled:opacity-50"
                        title={t('quote.refresh', 'Xem châm ngôn khác')}
                    >
                        {isLoading ? (
                            <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
                        ) : (
                            <RefreshCw className="w-4 h-4 text-amber-600 group-hover:rotate-180 transition-transform duration-300" />
                        )}
                    </button>
                </div>
            </div>

            {/* Quote Content */}
            <div className="flex-1 flex flex-col justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 rounded-xl p-5 border border-amber-100">
                {isLoading && !quote ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                    </div>
                ) : quote ? (
                    <>
                        <div className="relative">
                            <span className="absolute -top-3 -left-2 text-5xl text-amber-200 font-serif leading-none select-none">
                                "
                            </span>

                            <p className="text-gray-700 text-sm leading-relaxed pl-5 pr-3 italic">
                                {showTranslation && quote.translatedQuote
                                    ? quote.translatedQuote
                                    : quote.quote}
                            </p>
                            {showTranslation && quote.translatedQuote && (
                                <p className="text-gray-400 text-xs leading-relaxed pl-5 pr-3 italic mt-2 border-t border-amber-100 pt-2">
                                    {quote.quote}
                                </p>
                            )}

                            <span className="absolute -bottom-5 right-0 text-5xl text-amber-200 font-serif leading-none select-none">
                                "
                            </span>
                        </div>

                        {/* Author & Source */}
                        <div className="mt-6 flex items-center justify-end gap-2">
                            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-300" />
                            <div className="text-right">
                                <Typogaphy className="text-sm font-medium text-amber-700 whitespace-nowrap">
                                    — {quote.author}
                                </Typogaphy>
                                {quote.source === 'user' &&
                                    quote.contributedBy && (
                                        <Typogaphy className="text-[10px] text-amber-500">
                                            {t(
                                                'quote.contributed-by',
                                                'Đóng góp bởi'
                                            )}{' '}
                                            {quote.contributedBy}
                                        </Typogaphy>
                                    )}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-center text-gray-500 text-sm">
                        {error || t('quote.no-data', 'Không có dữ liệu')}
                    </div>
                )}
            </div>

            {/* Contribute Modal */}
            {showContributeModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {t(
                                    'quote.contribute-title',
                                    'Đóng góp châm ngôn'
                                )}
                            </h3>
                            <button
                                onClick={() => setShowContributeModal(false)}
                                className="p-1 hover:bg-gray-100 rounded-full"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        {submitSuccess ? (
                            <div className="text-center py-8">
                                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <QuoteIcon className="w-6 h-6 text-green-600" />
                                </div>
                                <p className="text-green-600 font-medium">
                                    {t(
                                        'quote.submit-success',
                                        'Cảm ơn bạn đã đóng góp!'
                                    )}
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('quote.content-label')}{' '}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>
                                        <textarea
                                            value={newQuote}
                                            onChange={(e) =>
                                                setNewQuote(e.target.value)
                                            }
                                            onKeyDown={(e) =>
                                                e.stopPropagation()
                                            }
                                            placeholder={t(
                                                'quote.content-placeholder'
                                            )}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-none text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('quote.author-label', 'Tác giả')}
                                        </label>
                                        <input
                                            type="text"
                                            value={newAuthor}
                                            onChange={(e) =>
                                                setNewAuthor(e.target.value)
                                            }
                                            onKeyDown={(e) =>
                                                e.stopPropagation()
                                            }
                                            placeholder={t(
                                                'quote.author-placeholder',
                                                'Nhập tên tác giả (mặc định: Unknown)'
                                            )}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none text-sm"
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2 mt-6">
                                    <Button
                                        variant="outlined"
                                        onClick={() =>
                                            setShowContributeModal(false)
                                        }
                                    >
                                        {t('common.cancel', 'Huỷ')}
                                    </Button>
                                    <Button
                                        onClick={handleSubmitQuote}
                                        disabled={
                                            isSubmitting || !newQuote.trim()
                                        }
                                    >
                                        {isSubmitting ? (
                                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        ) : (
                                            <Send className="w-4 h-4 mr-2" />
                                        )}
                                        {t('common.submit', 'Gửi')}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default QuoteOfTheDay
